import { useQuery } from '@tanstack/react-query';
import type { TDMTypes } from '@tf/api';
import { TDMClient } from '@tf/api';
import constate from 'constate';
import type React from 'react';

import { useConfig } from '@/core/providers/index';
import { QueryLoader } from '@/core/providers/QueryLoader';

type CompanyLandingSettings = {
	mode: 'companyLanding';
	tenant: string;
	defaultLocale: string;
};

type EndClientAreaSettings = {
	mode: 'endClientArea';
};

type Settings = CompanyLandingSettings | EndClientAreaSettings;

const [SettingsStateProvider, useSettingsState] = constate((props: { value: Settings }) => {
	return props.value;
});

export const useSettings = useSettingsState;

export const SettingsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const config = useConfig();

	const settingsQuery = useQuery({
		enabled: !!config.urls.tdm,
		queryKey: ['settings'],
		queryFn: async (): Promise<Settings> => {
			TDMClient.init(config.urls.tdm);

			try {
				const res = await TDMClient.getInstance().getClientAreaSettings();
				const data: TDMTypes.GetClientAreaSettingsData = await res.json();

				return {
					mode: 'companyLanding',
					tenant: data.tenant,
					defaultLocale: data.settings.default_locale,
				};
			} catch (e: any) {
				// No Client Area settings found for this domain - it means this is a free tier tenant
				if (e.status === 400) {
					return {
						mode: 'endClientArea',
					};
				}

				throw e;
			}
		},
	});

	return (
		<QueryLoader
			query={settingsQuery}
			errorMessage="Failed to load settings"
			renderData={(data) => {
				return <SettingsStateProvider value={data}>{children}</SettingsStateProvider>;
			}}
		/>
	);
};
